import { FormControl, FormGroup } from '@angular/forms';
import { ReportExtensions } from '@spa/reporting';
import { MaximumNumberValidatorKey, MinimumNumberValidatorKey } from 'appEntries/panel/validators';
import { environment } from 'appEnv/environment';
import * as moment from 'moment';

export abstract class BaseSubProductFrom<TControls, TEntity> {
    public form: FormGroup;
    public controls: TControls;

    constructor(protected numberFieldNames: string[], protected dateFieldNames?: string[]) {
    }

    protected abstract getFormControls(): TControls;
    protected abstract postInitForm(): void;
    protected abstract postUpdateFormValues(entity: TEntity): void;
    protected abstract customValidateForm(messageFunction: (controlsLabel?: string) => { [key: string]: string }): boolean;

    public initForm(): void {
        this.form = new FormGroup({});
        this.controls = this.getFormControls();

        Object.keys(this.controls).forEach(key => {
            const control = this.controls[key];

            this.form.addControl(control.name, new FormControl(control.value, control.validators));
        });

        this.postInitForm();
    }

    public validateForm(messageFunction: (controlsLabel?: string, controlLength?: number) => { [key: string]: string }): boolean {
        const customValidation = this.customValidateForm(messageFunction);

        if (this.form.invalid) {
            Object.keys(this.controls).forEach(key => {
                const control = this.controls[key];
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors).map(errorKey => {
                        const name = control.placeholder.replace(' *', '');

                        if (errorKey === MinimumNumberValidatorKey) {
                            const minError = formControl.errors[errorKey];

                            return messageFunction(name, minError.min)[errorKey];
                        }

                        if (errorKey === MaximumNumberValidatorKey) {
                            const maxError = formControl.errors[errorKey];

                            return messageFunction(name, maxError.max)[errorKey];
                        }

                        return messageFunction(name)[errorKey];
                    });
                }
            });

            return false;
        }

        return customValidation;
    }

    public updateFormValues(entity: TEntity) {
        Object.keys(this.controls).forEach(key => {
            const control = this.controls[key];
            const formControl = this.form.controls[key];

            if (entity.hasOwnProperty(key)) {
                if (this.numberFieldNames.includes(key)) {
                    if (!entity[key] && entity[key] !== 0) {
                        control.value = null;
                    }
                    else {
                        control.value = ReportExtensions.toDecimalString(entity[key], 2, 2, false);
                    }
                }
                else if (this.dateFieldNames && this.dateFieldNames.includes(key)) {
                    if (!entity[key]) {
                        control.value = null;
                    }
                    else {
                        control.value = moment(entity[key]).format(environment.dateFormat);
                    }
                }
                else {
                    control.value = entity[key];
                }

                formControl.setValue(control.value);
            }
            else {
                control.value = null;
                formControl.setValue(null);
            }
        });

        this.postUpdateFormValues(entity);
    }
}
